import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';

import { initSupabaseClient } from './supabaseClient';

type SupabaseConfig = {
  url: string;
  anonKey: string;
};

function sessionToUser(session: Session | null) {
  if (!session) {
    return null;
  }
  return {
    access_token: session.access_token,
    supabaseUser: session.user,
  };
}

type Props = {
  supabase: SupabaseConfig;
  userAuthenticationService: {
    getUser: () => unknown;
    setUser: (user: unknown) => void;
    set: (state: Record<string, unknown>) => void;
    setServiceImplementation: (impl: Record<string, unknown>) => void;
  };
};

/**
 * Enables Supabase session sync and wires UserAuthenticationService like OIDC does.
 */
function SupabaseAuthRoutes({ supabase, userAuthenticationService }: Props) {
  const navigate = useNavigate();

  useEffect(() => {
    const supabaseClient = initSupabaseClient(supabase.url, supabase.anonKey);

    const getAuthorizationHeader = () => {
      const user = userAuthenticationService.getUser() as { access_token?: string } | null;
      if (!user?.access_token) {
        return;
      }
      return {
        Authorization: `Bearer ${user.access_token}`,
      };
    };

    const handleUnauthenticated = () => {
      navigate('/sign-in', { replace: true });
      return null;
    };

    userAuthenticationService.set({ enabled: true });
    userAuthenticationService.setServiceImplementation({
      getAuthorizationHeader,
      handleUnauthenticated,
    });

    const applySession = (session: Session | null) => {
      userAuthenticationService.setUser(sessionToUser(session));
    };

    supabaseClient.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    });

    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, supabase.anonKey, supabase.url, userAuthenticationService]);

  return null;
}

export default SupabaseAuthRoutes;
